import { useCallback } from "react";
import { deviceMotionBodyToCameraFrameTransform } from "../Utils/constants";

// Constants
const GRAVITY_MS2 = 9.81;

// Types
type Vector3D = [number, number, number];
type Matrix3x3 = [Vector3D, Vector3D, Vector3D];

interface GravitySample {
  x: number;
  y: number;
  z: number;
}

interface GravityStats {
  mean: GravitySample;
  variance: GravitySample;
}

export const useGravityUtils = () => {
  const convertToG = useCallback((sample: GravitySample): GravitySample => ({
    x: sample.x / GRAVITY_MS2,
    y: sample.y / GRAVITY_MS2,
    z: sample.z / GRAVITY_MS2,
  }), []);

  const computeStatistics = useCallback(
    (buffer: GravitySample[]): GravityStats => {
      if (buffer.length === 0) {
        return {
          mean: { x: 0, y: 0, z: 0 },
          variance: { x: 0, y: 0, z: 0 },
        };
      }

      // Single pass calculation for means and squared sums
      const { sums, squareSums } = buffer.reduce(
        (acc, val) => ({
          sums: {
            x: acc.sums.x + val.x,
            y: acc.sums.y + val.y,
            z: acc.sums.z + val.z,
          },
          squareSums: {
            x: acc.squareSums.x + val.x * val.x,
            y: acc.squareSums.y + val.y * val.y,
            z: acc.squareSums.z + val.z * val.z,
          },
        }),
        {
          sums: { x: 0, y: 0, z: 0 },
          squareSums: { x: 0, y: 0, z: 0 },
        }
      );

      const len = buffer.length;
      const mean = {
        x: sums.x / len,
        y: sums.y / len,
        z: sums.z / len,
      };

      const variance = {
        x: squareSums.x / len - mean.x * mean.x,
        y: squareSums.y / len - mean.y * mean.y,
        z: squareSums.z / len - mean.z * mean.z,
      };

      return { mean, variance };
    },
    []
  );

const normalizeGravity = useCallback(
    (gravity: GravitySample): GravitySample => {
      const magnitudeSq =
        gravity.x * gravity.x + gravity.y * gravity.y + gravity.z * gravity.z;

      if (magnitudeSq === 0) {
        return { x: 0, y: 0, z: 1 }; // Default to pointing down
      }

      const magnitude = Math.sqrt(magnitudeSq);
      return {
        x: gravity.x / magnitude,
        y: gravity.y / magnitude,
        z: gravity.z / magnitude,
      };
    },
    []
  );

  // Compute angle in degrees between two unit vectors (assumes both are normalized)
  const angleBetweenDeg = useCallback((a: GravitySample, b: GravitySample) => {
    const dot = a.x * b.x + a.y * b.y + a.z * b.z;
    const clamped = Math.max(-1, Math.min(1, dot));
    return (Math.acos(clamped) * 180) / Math.PI;
  }, []);

  // Root-mean-square of an array of numbers
  const rms = useCallback((arr: number[]) => {
    if (!arr.length) return 0;
    const sumSq = arr.reduce((acc, v) => acc + v * v, 0);
    return Math.sqrt(sumSq / arr.length);
  }, []);

  const multiplyMatrixAndVector = useCallback((vector: Vector3D): Vector3D => {
    const matrix = deviceMotionBodyToCameraFrameTransform as Matrix3x3;
    return [
      matrix[0][0] * vector[0] +
        matrix[0][1] * vector[1] +
        matrix[0][2] * vector[2],
      matrix[1][0] * vector[0] +
        matrix[1][1] * vector[1] +
        matrix[1][2] * vector[2],
      matrix[2][0] * vector[0] +
        matrix[2][1] * vector[1] +
        matrix[2][2] * vector[2],
    ];
  }, []);

  return {
    convertToG,
    computeStatistics,
    normalizeGravity,
    multiplyMatrixAndVector,
    angleBetweenDeg,
    rms,
  };
};
